import { List, ListRow, PageHeader, Section, Skeleton, StatCard, Text } from '@ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useLocations } from '../apiary/api';
import { BloomingCard } from '../forage/components/BloomingCard';
import { WeatherCard } from '../forage/components/WeatherCard';
import { useBusiness, useMembers } from '../identity/api';
import { useSession } from '../identity/session';
import { formatKg } from '../harvest/format';
import { useDashboard } from '../reports/api';
import { change } from '../reports/format';
import { useInvoices, useMyMarketDays } from '../sales/api';
import { MyMarketDays } from '../sales/components/MyMarketDays';
import { formatEuro } from '../sales/format';
import { useTasks } from '../tasks/api';
import { TaskList } from '../tasks/components/TaskList';
import styles from './DashboardPage.module.css';

/**
 * What is due today, then a few key figures (docs/ux-richtlinien.md §6.13).
 * Tiles without data are left out.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const dashboard = useDashboard();
  const locations = useLocations(false, can('locations.read'));
  const homeLocation =
    locations.data?.find((l) => l.kind === 'home') ?? locations.data?.[0] ?? undefined;
  const d = dashboard.data;

  return (
    <div className={styles.page}>
      <PageHeader title={t('dashboard.title')} />
      <Setup />
      <Today
        runningTreatments={d?.colonies?.running_treatments ?? 0}
        loading={dashboard.isPending}
      />

      {d && (d.colonies || d.stock || d.sales || d.yield) && (
        <section aria-label={t('dashboard.figures')} className={styles.figures}>
          {d.colonies && (
            <StatCard
              label={t('dashboard.tiles.activeColoniesLabel')}
              value={d.colonies.active}
              href="/voelker"
            />
          )}
          {d.stock && (
            <StatCard
              label={t('dashboard.tiles.stockLabel')}
              value={formatKg(
                d.stock.bulk.reduce((sum, b) => sum + b.value, 0),
                1,
              )}
              footnote={
                d.stock.jars.length > 0
                  ? t('dashboard.tiles.jarsShort', {
                      count: d.stock.jars.reduce((sum, j) => sum + j.value, 0),
                    })
                  : undefined
              }
              href="/bestand"
            />
          )}
          {d.sales && (
            <StatCard
              label={t('dashboard.tiles.revenueLabel', { year: d.sales.year })}
              value={formatEuro(d.sales.revenue_cents)}
              delta={change(
                d.sales.revenue_cents,
                d.sales.previous_revenue_cents,
                t('dashboard.tiles.vsSameDay', { year: d.sales.year - 1 }),
              )}
              footnote={t('dashboard.tiles.previousYear', {
                amount: formatEuro(d.sales.previous_revenue_cents),
              })}
              href="/verkauf"
            />
          )}
          {d.yield && (
            <StatCard
              label={t('dashboard.tiles.yieldLabel', { year: d.yield.year })}
              value={formatKg(d.yield.grams, 1)}
              delta={change(
                d.yield.grams,
                d.yield.previous_grams,
                t('dashboard.tiles.vsYear', { year: d.yield.year - 1 }),
              )}
              footnote={t('dashboard.tiles.previousYear', {
                amount: formatKg(d.yield.previous_grams, 1),
              })}
              href="/ernte"
            />
          )}
        </section>
      )}

      <div className={styles.columns}>
        {homeLocation && (
          <WeatherCard locationId={homeLocation.id} title={homeLocation.name} hideWhenUnavailable />
        )}
        {can('locations.read') && <BloomingCard hideWhenEmpty />}
      </div>
    </div>
  );
}

/** Open setup steps; disappears when done. */
function Setup() {
  const { t } = useTranslation();
  const { can } = useSession();
  const canSetUp = can('business.write');
  const canInvite = can('members.manage');
  const business = useBusiness();
  const members = useMembers(canSetUp && canInvite);
  const steps = [
    ...(canSetUp && business.data && !business.data.address
      ? [{ key: 'profile', label: t('dashboard.setup.profile'), href: '/einstellungen/imkerei' }]
      : []),
    ...(canSetUp && canInvite && members.data && members.data.length <= 1
      ? [{ key: 'team', label: t('dashboard.setup.team'), href: '/einstellungen/mitglieder' }]
      : []),
  ];
  if (steps.length === 0) return null;
  return (
    <Section title={t('dashboard.setup.title')}>
      <List>
        {steps.map((s) => (
          <ListRow key={s.key} title={s.label} href={s.href} />
        ))}
      </List>
    </Section>
  );
}

/** Tasks due today, running treatments and overdue invoices (TSK-003, INVC-011). */
function Today({ runningTreatments, loading }: { runningTreatments: number; loading: boolean }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const tasks = useTasks({ view: 'today' }, can('tasks.read'));
  const invoices = useInvoices({ overdue: true }, can('invoices.read'));
  const marketDays = useMyMarketDays(can('sales.read'));
  const rows = tasks.data ?? [];
  const overdue = invoices.data ?? [];
  const markets = marketDays.data ?? [];
  const pending = loading || tasks.isPending;
  const nothing =
    !pending &&
    rows.length === 0 &&
    runningTreatments === 0 &&
    overdue.length === 0 &&
    markets.length === 0;

  return (
    <Section
      title={t('dashboard.today.title')}
      {...(can('tasks.read')
        ? { action: <Link to="/aufgaben">{t('dashboard.today.all')}</Link> }
        : {})}
    >
      {pending ? (
        <div className={styles.loading} role="status" aria-label={t('common.loading')}>
          <Skeleton lines={2} />
        </div>
      ) : nothing ? (
        <Text tone="muted" className={styles.empty}>
          {t('dashboard.today.empty')}
        </Text>
      ) : (
        <>
          {markets.length > 0 && <MyMarketDays days={markets} />}
          {(runningTreatments > 0 || overdue.length > 0) && (
            <List>
              {runningTreatments > 0 && (
                <ListRow
                  title={t('dashboard.tiles.runningTreatments', { count: runningTreatments })}
                  href="/voelker/behandlungen"
                />
              )}
              {overdue.length > 0 && (
                <ListRow
                  title={t('dashboard.overdue.row', { count: overdue.length })}
                  trailing={
                    <Text as="span" tone="danger" numeric>
                      {formatEuro(overdue.reduce((sum, i) => sum + i.total_cents, 0))}
                    </Text>
                  }
                  href="/verkauf/rechnungen?ueberfaellig=1"
                />
              )}
            </List>
          )}
          {rows.length > 0 && <TaskList tasks={rows.slice(0, 6)} />}
        </>
      )}
    </Section>
  );
}
